import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  PillowSize,
  NailheadSize,
  CustomizationStreamResult,
  CustomizationStreamProgress,
  CustomizationStreamStatus,
  CustomizationParams,
  CustomizationFiles,
} from '../../types/customization.types';

export interface SwatchesData {
  frameFabrics: string[];
  seatFabrics: string[];
  backCushionFabrics: string[];
  armrestFabrics: string[];
  woodFinishes: string[];
  nailheadSizes: string[];

  // Demo-2 product swatches
  ralphTwoFabric: string[];
  ralphSofaChairFinishes: string[];
  ralphChairSeatFabrics: string[];
  ralphChairBackFabrics: string[];
  ralphChairArmrestFabrics: string[];
}

export interface CustomizationState {
  swatches: SwatchesData;
  swatchesLoading: boolean;
  swatchesError: string | null;

  selectedProduct: string | null;

  frameFabricUrl: string | null;
  backCushionFabricUrl: string | null;
  seatFabricUrl: string | null;
  armrestFabricUrl: string | null;
  finishUrl: string | null;

  pillowFile: File | null;
  pillowSize: PillowSize | null;
  pillowCount: number | null;
  pillowFabricUrl: string | null;

  nailheadFile: File | null;
  nailheadSize: NailheadSize | null;

  streamStatus: CustomizationStreamStatus;
  streamResults: CustomizationStreamResult[];
  streamErrors: Array<{ index: number; filename: string; error: string }>;
  streamProgress: CustomizationStreamProgress | null;
  streamError: string | null;

  regeneratingIndex: number | null;
  lastCustomizationPayload: { params: CustomizationParams; files: CustomizationFiles } | null;
}

const initialState: CustomizationState = {
  swatches: {
    frameFabrics: [],
    seatFabrics: [],
    backCushionFabrics: [],
    armrestFabrics: [],
    woodFinishes: [],
    nailheadSizes: [],

    ralphTwoFabric: [],
    ralphSofaChairFinishes: [],
    ralphChairSeatFabrics: [],
    ralphChairBackFabrics: [],
    ralphChairArmrestFabrics: [],
  },
  swatchesLoading: false,
  swatchesError: null,

  selectedProduct: null,

  frameFabricUrl: null,
  backCushionFabricUrl: null,
  seatFabricUrl: null,
  armrestFabricUrl: null,
  finishUrl: null,

  pillowFile: null,
  pillowSize: null,
  pillowCount: null,
  pillowFabricUrl: null,

  nailheadFile: null,
  nailheadSize: null,

  streamStatus: 'idle',
  streamResults: [],
  streamErrors: [],
  streamProgress: null,
  streamError: null,

  regeneratingIndex: null,
  lastCustomizationPayload: null,
};

const customizationSlice = createSlice({
  name: 'customization',
  initialState,
  reducers: {
    fetchSwatchesRequest: (state) => {
      state.swatchesLoading = true;
      state.swatchesError = null;
    },
    fetchSwatchesSuccess: (state, action: PayloadAction<SwatchesData>) => {
      state.swatchesLoading = false;
      state.swatches = action.payload;
    },
    fetchSwatchesFailure: (state, action: PayloadAction<string>) => {
      state.swatchesLoading = false;
      state.swatchesError = action.payload;
    },

    setSelectedProduct: (state, action: PayloadAction<string | null>) => {
      state.selectedProduct = action.payload;
    },

    setFrameFabricUrl: (state, action: PayloadAction<string | null>) => {
      state.frameFabricUrl = action.payload;
    },
    setBackCushionFabricUrl: (state, action: PayloadAction<string | null>) => {
      state.backCushionFabricUrl = action.payload;
    },
    setSeatFabricUrl: (state, action: PayloadAction<string | null>) => {
      state.seatFabricUrl = action.payload;
    },
    setArmrestFabricUrl: (state, action: PayloadAction<string | null>) => {
      state.armrestFabricUrl = action.payload;
    },
    setFinishUrl: (state, action: PayloadAction<string | null>) => {
      state.finishUrl = action.payload;
    },

    setPillowFile: (state, action: PayloadAction<File | null>) => {
      state.pillowFile = action.payload;
    },
    setPillowSize: (state, action: PayloadAction<PillowSize | null>) => {
      state.pillowSize = action.payload;
    },
    setPillowCount: (state, action: PayloadAction<number | null>) => {
      state.pillowCount = action.payload;
    },
    setPillowFabricUrl: (state, action: PayloadAction<string | null>) => {
      state.pillowFabricUrl = action.payload;
    },

    setNailheadFile: (state, action: PayloadAction<File | null>) => {
      state.nailheadFile = action.payload;
    },
    setNailheadSize: (state, action: PayloadAction<NailheadSize | null>) => {
      state.nailheadSize = action.payload;
    },

    // Customization stream actions
    customizeRequest: (state, action: PayloadAction<{ params: CustomizationParams; files: CustomizationFiles }>) => {
      state.streamStatus = 'streaming';
      state.streamResults = [];
      state.streamErrors = [];
      state.streamProgress = null;
      state.streamError = null;
      state.lastCustomizationPayload = action.payload;
    },
    customizeStreamResult: (state, action: PayloadAction<CustomizationStreamResult>) => {
      state.streamResults.push(action.payload);
    },
    customizeStreamError: (state, action: PayloadAction<{ index: number; filename: string; error: string }>) => {
      state.streamErrors.push(action.payload);
    },
    customizeStreamProgress: (state, action: PayloadAction<CustomizationStreamProgress>) => {
      state.streamProgress = action.payload;
      state.streamStatus = 'completed';
    },
    customizeFailure: (state, action: PayloadAction<string>) => {
      state.streamError = action.payload;
      state.streamStatus = 'failed';
    },
    customizeCancel: (state) => {
      state.streamStatus = 'idle';
    },
    resetStream: (state) => {
      state.streamStatus = 'idle';
      state.streamResults = [];
      state.streamErrors = [];
      state.streamProgress = null;
      state.streamError = null;
    },

    // Regenerate actions
    regenerateImageRequest: (state, action: PayloadAction<{ cacheHash: string; imageIndex: number }>) => {
      state.regeneratingIndex = action.payload.imageIndex;
    },
    regenerateImageDeleteSuccess: (state) => {
      // Cache deleted, about to re-trigger bulk API
      state.streamStatus = 'streaming';
      state.streamResults = [];
      state.streamErrors = [];
      state.streamProgress = null;
      state.streamError = null;
    },
    regenerateImageComplete: (state) => {
      state.regeneratingIndex = null;
    },
    regenerateImageFailure: (state, action: PayloadAction<string>) => {
      state.regeneratingIndex = null;
      state.streamError = action.payload;
    },

    clearCustomizations: (state) => {
      state.frameFabricUrl = null;
      state.backCushionFabricUrl = null;
      state.seatFabricUrl = null;
      state.armrestFabricUrl = null;
      state.finishUrl = null;
      state.pillowFile = null;
      state.pillowSize = null;
      state.pillowCount = null;
      state.pillowFabricUrl = null;
      state.nailheadFile = null;
      state.nailheadSize = null;
    },

    clearAll: () => initialState,
  },
});

export const {
  fetchSwatchesRequest,
  fetchSwatchesSuccess,
  fetchSwatchesFailure,
  setSelectedProduct,
  setFrameFabricUrl,
  setBackCushionFabricUrl,
  setSeatFabricUrl,
  setArmrestFabricUrl,
  setFinishUrl,
  setPillowFile,
  setPillowSize,
  setPillowCount,
  setPillowFabricUrl,
  setNailheadFile,
  setNailheadSize,
  customizeRequest,
  customizeStreamResult,
  customizeStreamError,
  customizeStreamProgress,
  customizeFailure,
  customizeCancel,
  resetStream,
  regenerateImageRequest,
  regenerateImageDeleteSuccess,
  regenerateImageComplete,
  regenerateImageFailure,
  clearCustomizations,
  clearAll,
} = customizationSlice.actions;

export const customizationReducer = customizationSlice.reducer;
export default customizationSlice.reducer;
